import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * ScrollToTop component that automatically scrolls to the top of the page
 * whenever the route changes. This ensures users start at the top when
 * navigating to a new page.
 */
export const ScrollToTop: React.FC = () => {
    const { pathname } = useLocation()

    useEffect(() => {
        // Comprehensive scroll to top function that handles multiple scenarios
        const scrollToTop = () => {
            // Try multiple methods to ensure scrolling works
            try {
                // Temporarily disable smooth scrolling to ensure instant scroll
                const originalScrollBehavior = document.documentElement.style.scrollBehavior
                document.documentElement.style.scrollBehavior = 'auto'

                // Method 1: Scroll window with instant behavior
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'instant'
                })

                // Method 2: Also scroll document.documentElement
                if (document.documentElement) {
                    document.documentElement.scrollTop = 0
                    document.documentElement.scrollLeft = 0
                }

                // Method 3: Also scroll document.body
                if (document.body) {
                    document.body.scrollTop = 0
                    document.body.scrollLeft = 0
                }

                // Method 4: Force scroll with setTimeout to override any CSS smooth behavior
                setTimeout(() => {
                    window.scrollTo(0, 0)
                    if (document.documentElement) {
                        document.documentElement.scrollTop = 0
                    }
                    if (document.body) {
                        document.body.scrollTop = 0
                    }
                }, 0)

                // Restore original scroll behavior after a short delay
                setTimeout(() => {
                    document.documentElement.style.scrollBehavior = originalScrollBehavior
                }, 50)
            } catch (error) {
                // Fallback to basic scroll
                window.scrollTo(0, 0)
            }
        }

        // Scroll immediately
        scrollToTop()

        // Also scroll after a small delay to handle any delayed content rendering
        const timeoutId = setTimeout(scrollToTop, 100)

        // Use requestAnimationFrame as a fallback
        const rafId = requestAnimationFrame(scrollToTop)

        return () => {
            clearTimeout(timeoutId)
            cancelAnimationFrame(rafId)
        }
    }, [pathname])

    return null
} 